import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import { confirmDelete } from '../../../utils/ecxSwal';
import patientService from '../../../services/patient.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import EcxDatePicker from '../../common/EcxDatePicker';
import {
    DEFAULT_INVALID_DETAIL_FILTERS,
    DEFAULT_INVALID_DETAIL_SORT,
    INVALID_DETAIL_SORT_FIELDS,
    INVALID_DETAIL_TYPES,
    buildInvalidDetailsQuery,
    formatInvalidDetailPatientName,
    getInvalidDetailTypeMeta,
} from './subparts/invalidDetailsQuery';
import InvalidDetailsSkeleton from './subparts/InvalidDetailsSkeleton';
import {
    buildInvalidDetailsPrintSubtitle,
    printInvalidDetailsList,
} from './subparts/invalidDetailsPrint';
import './InvalidDetails.css';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function InvalidDetails() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const setInvalidDetailsCount = useEyeconxStore((state) => state.setInvalidDetailsCount);

    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const [filters, setFilters] = useState(DEFAULT_INVALID_DETAIL_FILTERS);
    const [appliedFilters, setAppliedFilters] = useState(DEFAULT_INVALID_DETAIL_FILTERS);
    const [sort, setSort] = useState(DEFAULT_INVALID_DETAIL_SORT);

    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);

    const fetchRecords = useCallback(
        async (page, size, nextFilters, nextSort, options = {}) => {
            const { silent = false } = options;

            if (!clinicId) {
                return;
            }

            if (!silent) {
                setLoading(true);
            }

            try {
                const query = buildInvalidDetailsQuery(nextFilters, nextSort);
                const res = await patientService.getInvalidDetailsList(clinicId, page, size, query);
                const data = res.data;
                const content = data.content || [];

                setRecords(content);
                setPageNumber(data.number ?? page);
                setPageSize(data.size ?? size);
                setTotalPages(data.totalPages ?? 0);
                setTotalElements(data.totalElements ?? 0);
                setInvalidDetailsCount(data.totalElements ?? 0);
                setSelectedIds(new Set());
            } catch (err) {
                console.error('Failed to load invalid details', err);
                toast.error('Failed to load invalid details');
            } finally {
                if (!silent) {
                    setLoading(false);
                }
            }
        },
        [clinicId, setInvalidDetailsCount]
    );

    useEffect(() => {
        fetchRecords(pageNumber, pageSize, appliedFilters, sort);
    }, [fetchRecords, pageNumber, pageSize, appliedFilters, sort]);

    const handleApplyFilters = (nextFilters = filters) => {
        setAppliedFilters(nextFilters);
        setPageNumber(0);
    };

    const handleQuickFilter = (field, value) => {
        const next = { ...filters, [field]: value };
        setFilters(next);
        setAppliedFilters(next);
        setPageNumber(0);
    };

    const handleClearFilters = () => {
        setFilters(DEFAULT_INVALID_DETAIL_FILTERS);
        setAppliedFilters(DEFAULT_INVALID_DETAIL_FILTERS);
        setPageNumber(0);
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        try {
            await fetchRecords(pageNumber, pageSize, appliedFilters, sort, { silent: true });
        } finally {
            setIsRefreshing(false);
        }
    };

    const handleSortChange = (field, value) => {
        const next = { ...sort, [field]: value };
        setSort(next);
        setPageNumber(0);
    };

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const toggleSelect = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (selectedIds.size === records.length) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(records.map((item) => item.id)));
        }
    };

    const handleDelete = async (ids) => {
        if (!ids.length) {
            toast.info('No records selected.');
            return;
        }

        const isBulk = ids.length > 1;
        const result = await confirmDelete({
            title: isBulk ? 'Delete Invalid Details?' : 'Delete Invalid Detail?',
            itemName: isBulk ? `${ids.length} invalid detail records` : 'this invalid detail record',
            description: 'Removed records will no longer appear in the invalid details log.',
            onConfirm: () => patientService.deleteInvalidDetails(clinicId, ids),
        });

        if (result.isConfirmed) {
            fetchRecords(pageNumber, pageSize, appliedFilters, sort);
        }
    };

    const allSelected = records.length > 0 && selectedIds.size === records.length;

    const toolbarMeta = useMemo(
        () => ({
            pageLabel: `Page ${totalPages === 0 ? 0 : pageNumber + 1} of ${Math.max(totalPages, 1)}`,
            totalLabel: `${totalElements} record${totalElements === 1 ? '' : 's'}`,
        }),
        [pageNumber, totalPages, totalElements]
    );

    const handlePrint = () => {
        const printed = printInvalidDetailsList({
            records,
            title: 'Invalid Details',
            subtitle: buildInvalidDetailsPrintSubtitle({
                recordsCount: records.length,
                totalElements,
                pageNumber,
                totalPages,
                appliedFilters,
            }),
        });

        if (!printed) {
            toast.error('Unable to open print preview. Please try again.');
        }
    };

    const description = useMemo(
        () =>
            totalElements
                ? `Invalid patient contact information · ${totalElements} total record${totalElements === 1 ? '' : 's'}`
                : 'Review and resolve invalid patient contact information',
        [totalElements]
    );

    return (
        <div className="ecx-page invalid-details-page ecx-page--full-width">
            <SubHeader
                title="Invalid Details"
                description={description}
                icon="bi-exclamation-octagon"
                containerClass="w-100 px-4"
            >
                <button
                    type="button"
                    className="btn btn-light-primary btn-sm rounded-pill"
                    onClick={handleRefresh}
                    disabled={loading || isRefreshing}
                    title="Refresh invalid details"
                >
                    <i className={`bi bi-arrow-clockwise me-1 ${isRefreshing ? 'ecx-refresh-spin' : ''}`}></i>
                    Refresh
                </button>
            </SubHeader>

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel invalid-details-panel">
                    <div className="invalid-details-filters">
                        <div className="invalid-details-filters__row">
                            <div className="invalid-details-filters__search input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-person text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Search patient name..."
                                    value={filters.patientName}
                                    onChange={(e) => setFilters((prev) => ({ ...prev, patientName: e.target.value }))}
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                    disabled={loading}
                                />
                            </div>

                            <div className="invalid-details-filters__search input-group input-group-sm">
                                <span className="input-group-text bg-white border-end-0">
                                    <i className="bi bi-search text-muted"></i>
                                </span>
                                <input
                                    type="text"
                                    className="form-control form-control-sm border-start-0"
                                    placeholder="Search detail value..."
                                    value={filters.detail}
                                    onChange={(e) => setFilters((prev) => ({ ...prev, detail: e.target.value }))}
                                    onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
                                    disabled={loading}
                                />
                                <button
                                    type="button"
                                    className="btn btn-sm btn-accent"
                                    onClick={() => handleApplyFilters()}
                                    disabled={loading}
                                >
                                    Search
                                </button>
                            </div>
                        </div>

                        <div className="invalid-details-filters__row">
                            <div className="invalid-details-filters__group">
                                <span className="invalid-details-filters__label">Type</span>
                                <div className="invalid-details-pills">
                                    {INVALID_DETAIL_TYPES.map((option) => (
                                        <button
                                            key={option.value}
                                            type="button"
                                            className={`invalid-details-pill ${
                                                filters.type === option.value ? 'is-active' : ''
                                            }`}
                                            onClick={() => handleQuickFilter('type', option.value)}
                                            disabled={loading}
                                        >
                                            <i className={`bi ${option.icon}`}></i>
                                            {option.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div className="invalid-details-filters__group invalid-details-filters__group--date">
                                <span className="invalid-details-filters__label">Date</span>
                                <EcxDatePicker
                                    className="invalid-details-filters__date-picker"
                                    value={filters.date}
                                    onChange={(value) => handleQuickFilter('date', value)}
                                    placeholder="Any date"
                                    disabled={loading}
                                    size="sm"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__header border-top-0">
                        <div className="invalid-details-toolbar">
                            <div className="invalid-details-toolbar__meta">
                                <h5 className="mb-0 fw-semibold text-dark">Invalid Detail Log</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-list-ul"></i>
                                    {toolbarMeta.totalLabel}
                                </span>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-layers"></i>
                                    {toolbarMeta.pageLabel}
                                </span>
                            </div>

                            <div className="invalid-details-toolbar__controls">
                                {selectedIds.size > 0 && (
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn"
                                        onClick={() => handleDelete(Array.from(selectedIds))}
                                        disabled={loading}
                                    >
                                        <i className="bi bi-trash me-1"></i>
                                        Delete selected ({selectedIds.size})
                                    </button>
                                )}

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Sort
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '9.5rem' }}
                                        value={sort.sortBy}
                                        onChange={(e) => handleSortChange('sortBy', e.target.value)}
                                        disabled={loading}
                                    >
                                        {INVALID_DETAIL_SORT_FIELDS.map((option) => (
                                            <option key={option.value} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '6.5rem' }}
                                        value={sort.sortOrder}
                                        onChange={(e) => handleSortChange('sortOrder', e.target.value)}
                                        disabled={loading}
                                    >
                                        <option value="DESC">Desc</option>
                                        <option value="ASC">Asc</option>
                                    </select>
                                </label>

                                <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                    Show
                                    <select
                                        className="form-select form-select-sm"
                                        style={{ width: '5rem' }}
                                        value={pageSize}
                                        onChange={handlePageSizeChange}
                                        disabled={loading}
                                    >
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <option key={size} value={size}>
                                                {size}
                                            </option>
                                        ))}
                                    </select>
                                    entries
                                </label>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handlePrint}
                                    disabled={loading || records.length === 0}
                                >
                                    <i className="bi bi-printer me-1"></i>
                                    Print
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleClearFilters}
                                    disabled={loading}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column p-0">
                        {loading ? (
                            <InvalidDetailsSkeleton count={pageSize > 8 ? 8 : pageSize} />
                        ) : records.length > 0 ? (
                            <>
                                <div className="table-responsive invalid-details-table-wrap">
                                    <table className="table table-hover align-middle mb-0 invalid-details-table">
                                        <thead>
                                            <tr>
                                                <th className="ps-3" style={{ width: '2.5rem' }}>
                                                    <input
                                                        type="checkbox"
                                                        className="form-check-input"
                                                        checked={allSelected}
                                                        onChange={toggleSelectAll}
                                                    />
                                                </th>
                                                <th>Type</th>
                                                <th>Patient</th>
                                                <th>Invalid Detail</th>
                                                <th>Created</th>
                                                <th className="text-end pe-3">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {records.map((item) => {
                                                const typeMeta = getInvalidDetailTypeMeta(item.type);
                                                const patient = item.patient || {};
                                                const patientName = formatInvalidDetailPatientName(patient);

                                                return (
                                                    <tr key={item.id}>
                                                        <td className="ps-3">
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedIds.has(item.id)}
                                                                onChange={() => toggleSelect(item.id)}
                                                            />
                                                        </td>
                                                        <td>
                                                            <span className={`invalid-detail-type ${typeMeta.className}`}>
                                                                <i className={`bi ${typeMeta.icon}`}></i>
                                                                {typeMeta.label}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {item.patientId ? (
                                                                <Link
                                                                    to={`/patient-files/${item.patientId}`}
                                                                    className="invalid-details-patient-link fw-semibold"
                                                                >
                                                                    {patientName}
                                                                </Link>
                                                            ) : (
                                                                <span className="fw-semibold text-dark">{patientName}</span>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <span className="invalid-details-value">{item.detail}</span>
                                                        </td>
                                                        <td className="text-muted small">{item.createdAt || '—'}</td>
                                                        <td className="text-end pe-3">
                                                            <button
                                                                type="button"
                                                                className="btn btn-sm btn-outline-secondary rounded-pill ecx-modal__close-btn"
                                                                onClick={() => handleDelete([item.id])}
                                                            >
                                                                <i className="bi bi-trash me-1"></i>
                                                                Delete
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>

                                {totalPages > 1 && (
                                    <div className="ecx-page-pagination border-top p-3 mt-auto">
                                        <Pagination
                                            currentPage={pageNumber}
                                            totalPages={totalPages}
                                            totalElements={totalElements}
                                            pageSize={pageSize}
                                            onPageChange={setPageNumber}
                                        />
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="ecx-page-empty flex-grow-1">
                                <div className="ecx-page-empty__icon">
                                    <i className="bi bi-check-circle"></i>
                                </div>
                                <h5 className="text-dark fw-semibold mb-2">No invalid details found</h5>
                                <p className="mb-0">
                                    {appliedFilters.patientName || appliedFilters.detail || appliedFilters.type !== 'ALL' || appliedFilters.date
                                        ? 'Try adjusting your filters to see more results.'
                                        : 'All patient contact details look good.'}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default InvalidDetails;
